import { AvatarGroup, type NumericSize, cn } from '@glade-ai/ui';
import { type ComponentPropsWithRef, forwardRef, useMemo } from 'react';

import { UserAvatar } from '@/containers/UserAvatar';
import renderParticipantsNames from '@/helpers/renderParticipantsNames';
import { useIsMobile } from '@/hooks';

type UsersAvatarGroupUser = {
  id: string;
  name?: string | null;
  logo?: string | null;
  color?: string | null;
  className?: string;
};

type UsersAvatarGroupProps = ComponentPropsWithRef<'div'> & {
  users?: Array<UsersAvatarGroupUser> | null;
  size?: NumericSize;
  maxLength?: number;
  noLabel?: boolean;
  showRemainingCount?: boolean;
  totalParticipants?: number;
  isJoin?: boolean;
  participantsToHighlight?: Array<string>;
  areAssignees?: boolean;
  labelClassName?: string;
};

/** Displays a horizontal group of user avatars with optional overflow count. */
const UsersAvatarGroup = forwardRef<HTMLDivElement, UsersAvatarGroupProps>(
  (
    {
      users,
      size = 6,
      maxLength = 3,
      noLabel = false,
      className,
      showRemainingCount = false,
      totalParticipants,
      isJoin = false,
      participantsToHighlight,
      areAssignees = false,
      labelClassName,
      ...props
    },
    forwardedRef,
  ) => {
    const isMobile = useIsMobile();

    const { filteredParticipants, iconsToDisplay, remainingCount } = useMemo(() => {
      if (!users || users.length === 0) {
        return { filteredParticipants: [], iconsToDisplay: 0, remainingCount: 0 };
      }

      const displayLimit = isMobile && maxLength >= 3 ? 3 : maxLength;

      // Sort participants: highlighted first, then with logo, then without
      const participantsToHighlightData = users.filter((participant) =>
        participantsToHighlight?.includes(participant.id),
      );
      const participantsWithLogo = users.filter(
        (participant) => participant.logo && !participantsToHighlight?.includes(participant.id),
      );
      const participantsWithoutLogo = users.filter(
        (participant) => !participant.logo && !participantsToHighlight?.includes(participant.id),
      );

      const sorted = [...participantsToHighlightData, ...participantsWithLogo, ...participantsWithoutLogo];
      const remaining = sorted.length - displayLimit;

      return {
        filteredParticipants: sorted,
        iconsToDisplay: displayLimit,
        remainingCount: remaining > 0 ? remaining : 0,
      };
    }, [users, isMobile, maxLength, participantsToHighlight]);

    if (!users || users.length === 0) {
      return null;
    }

    const displayedParticipants = filteredParticipants.slice(0, iconsToDisplay);

    const group = (
      <AvatarGroup>
        {displayedParticipants.map((participant) => {
          const isHighlighted = participantsToHighlight?.includes(participant.id);
          return (
            <UserAvatar
              key={participant.id}
              color={participant.color || undefined}
              url={participant.logo || undefined}
              size={size}
              name={participant.name || undefined}
              className={cn(
                isHighlighted && '!ring-2 !ring-[var(--color-warning)] !ring-offset-1 !ring-offset-background',
                participant.className,
              )}
            />
          );
        })}
        {showRemainingCount && remainingCount > 0 && (
          <div
            className={cn(
              'flex items-center justify-center rounded-full bg-[var(--color-gray-25)] text-[var(--color-gray-75)] text-[0.625rem] font-medium z-10 ring ring-background px-1',
            )}
            style={{
              height: size * 4,
              minWidth: size * 4,
            }}
          >
            +{remainingCount}
          </div>
        )}
      </AvatarGroup>
    );

    return (
      <div {...props} ref={forwardedRef} className={cn('flex items-center gap-2', className)}>
        {group}
        {!noLabel && (
          <p className={cn('gl-description', labelClassName)}>
            {renderParticipantsNames(
              filteredParticipants,
              totalParticipants || users.length,
              iconsToDisplay,
              isJoin,
              areAssignees,
            )}
          </p>
        )}
      </div>
    );
  },
);

UsersAvatarGroup.displayName = 'UsersAvatarGroup';

export { UsersAvatarGroup };
export type { UsersAvatarGroupProps, UsersAvatarGroupUser };
